import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { getOrdersSummary } from '../api/orders';
import { listSupportTickets } from '../api/support';
import { listReturnRequests, RETURN_STATUS } from '../api/returns';

const OrdersContext = createContext(null);

export function OrdersProvider({ children }) {
  const { user } = useAuth();
  const [queueBadge, setQueueBadge] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [inProgressTicketsCount, setInProgressTicketsCount] = useState(0);
  const [pendingReturnsCount, setPendingReturnsCount] = useState(0);
  const [toast, setToast] = useState(null);

  // Read the latest token/groups inside badge fetches without depending on idToken.
  // A 401 refresh rotates idToken; putting that in effect deps re-fired these
  // loaders in a loop whenever /store/returns (or similar) kept returning 401.
  const userRef = useRef(user);
  userRef.current = user;

  const userKey = user?.sub || null;

  const refreshQueueBadge = useCallback(async () => {
    const token = userRef.current?.idToken;
    if (!token) {
      setQueueBadge(0);
      return;
    }
    try {
      const summary = await getOrdersSummary(token);
      const received = summary.counts?.RECEIVED
        ?? summary.awaitingConfirmation
        ?? summary.queueBadge
        ?? 0;
      setQueueBadge(Number(received) || 0);
    } catch {
      // Keep the last known badge if the summary request fails.
    }
  }, [userKey]);

  const refreshTicketBadges = useCallback(async () => {
    const current = userRef.current;
    const token = current?.idToken;
    const allowed = !!current?.groups?.some((g) => ['Admin', 'Ops'].includes(g));
    if (!token || !allowed) {
      setOpenTicketsCount(0);
      setInProgressTicketsCount(0);
      return;
    }
    try {
      const [open, inProgress] = await Promise.all([
        listSupportTickets('OPEN', token, { enrichChannel: false }),
        listSupportTickets('IN_PROGRESS', token, { enrichChannel: false }),
      ]);
      setOpenTicketsCount(open.length);
      setInProgressTicketsCount(inProgress.length);
    } catch {
      // Keep the last known counts if the request fails.
    }
  }, [userKey]);

  const refreshReturnsBadge = useCallback(async () => {
    const current = userRef.current;
    const token = current?.idToken;
    const allowed = !!current?.groups?.some((g) => ['Admin', 'Ops'].includes(g));
    if (!token || !allowed) {
      setPendingReturnsCount(0);
      return;
    }
    try {
      const requests = await listReturnRequests(RETURN_STATUS.REQUESTED, token);
      setPendingReturnsCount(requests.length);
    } catch {
      // Keep last known count
    }
  }, [userKey]);

  useEffect(() => {
    refreshQueueBadge();
  }, [refreshQueueBadge]);

  useEffect(() => {
    refreshTicketBadges();
  }, [refreshTicketBadges]);

  useEffect(() => {
    refreshReturnsBadge();
  }, [refreshReturnsBadge]);

  const notify = useCallback((message) => {
    setToast({ message, id: Date.now() });
    setTimeout(() => setToast((t) => (t?.message === message ? null : t)), 2600);
  }, []);

  const value = useMemo(
    () => ({
      queueBadge,
      refreshQueueBadge,
      notify,
      toast,
      openTicketsCount,
      inProgressTicketsCount,
      refreshTicketBadges,
      pendingReturnsCount,
      refreshReturnsBadge,
    }),
    [queueBadge, refreshQueueBadge, notify, toast, openTicketsCount, inProgressTicketsCount, refreshTicketBadges, pendingReturnsCount, refreshReturnsBadge],
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}

export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used within OrdersProvider');
  return ctx;
}
